/*
 * Desktop dropdown fit.
 * A header dropdown taller than the space left below it in the window (e.g. a long brand list on a
 * 13" laptop at 125-150 % display scaling) is capped to that space and scrolls inside itself
 * (.dd-fit). Level-3 flyouts are kept inside the window: in a scrolling panel they are placed
 * position:fixed next to their item (the panel's overflow would otherwise clip them), elsewhere they
 * are only lifted when they would run past the bottom edge.
 * Nothing changes while everything fits, so tall screens render exactly as before.
 * Styles: assets/component-megamenu.css ("Dropdown fit").
 */
(() => {
    if (window.grivaDropdownFit) return;
    window.grivaDropdownFit = true;

    const ITEM = '.header__inline-menu .list-menu--inline > .menu-lv-1.dropdown';
    const GAP = 16;     // space kept free between a panel / flyout and the window edge
    const OVERLAP = 15; // .list-menu--disclosure-2 { margin-left: calc(100% - 15px) }
    const LIFT = 5;     // .list-menu--disclosure-2 { top: -5px }
    const desktop = window.matchMedia('(min-width: 1025px)');
    let current = null; // panel of the top-level item under the pointer
    let frame = 0;

    const panelOf = (item) => item.querySelector(':scope > .list-menu--disclosure-1');
    const flyoutOf = (sub) => sub.querySelector(':scope > .site-nav-list-dropdown > .list-menu--disclosure-2');

    function fitPanel(panel) {
        // offsetTop ignores the open animation's transform. Full list height: offsetHeight while the
        // panel is uncapped (scrollHeight would also count an open absolute flyout hanging below it),
        // scrollHeight once it scrolls (its flyouts are fixed then and do not count). Measuring in
        // place never resets the panel's scroll position.
        const top = panel.parentElement.getBoundingClientRect().top + panel.offsetTop;
        const room = Math.floor(window.innerHeight - top - GAP);
        const full = panel.classList.contains('dd-fit') ? panel.scrollHeight : panel.offsetHeight;
        if (full > room + 1) {
            panel.style.setProperty('--dd-max-h', Math.max(room, 120) + 'px');
            panel.classList.add('dd-fit');
        } else if (panel.classList.contains('dd-fit')) {
            panel.classList.remove('dd-fit');
            panel.style.removeProperty('--dd-max-h');
        }
    }

    function placeFlyout(panel, sub) {
        const fly = flyoutOf(sub);
        if (!fly) return;
        fly.style.removeProperty('top');
        const h = fly.offsetHeight;
        if (!h) return; // not open
        const r = sub.getBoundingClientRect();
        const bottom = window.innerHeight - GAP;
        if (panel.classList.contains('dd-fit')) {
            let x = r.right - OVERLAP;
            if (x + fly.offsetWidth > window.innerWidth - GAP) x = r.left - fly.offsetWidth + OVERLAP;
            const y = Math.max(GAP, Math.min(r.top - LIFT, bottom - h));
            fly.style.setProperty('--dd-fly-x', Math.round(x) + 'px');
            fly.style.setProperty('--dd-fly-y', Math.round(y) + 'px');
        } else {
            const f = fly.getBoundingClientRect();
            const lift = Math.min(f.bottom - bottom, f.top - GAP);
            if (lift > 0) fly.style.top = Math.round(-LIFT - lift) + 'px';
        }
    }

    function hoveredSub(panel) {
        return panel.querySelector(':scope > .menu-lv-2.dropdown:hover');
    }

    function refresh() {
        frame = 0;
        if (!current || !desktop.matches || !current.parentElement.matches(':hover')) return;
        fitPanel(current);
        const sub = hoveredSub(current);
        if (sub) placeFlyout(current, sub);
    }

    function schedule() {
        if (current && !frame) frame = requestAnimationFrame(refresh);
    }

    document.addEventListener('mouseover', (event) => {
        if (!desktop.matches || !(event.target instanceof Element)) return;
        const item = event.target.closest(ITEM);
        const panel = item && panelOf(item);
        if (!panel) {
            current = null;
            return;
        }
        // Re-measure whenever the top-level link is entered: the sticky header may have moved since.
        if (panel !== current || !panel.contains(event.target)) {
            current = panel;
            fitPanel(panel);
        }
        const sub = event.target.closest('.menu-lv-2.dropdown');
        if (sub && sub.parentElement === panel) placeFlyout(panel, sub);
    }, { passive: true });

    // Page scroll (sticky header moves), panel scroll (hovered item changes) and resize.
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
})();
